import { ChevronLeft, ChevronRight, Dot, Download, Save, Share } from "lucide-react";
import "./PlanificationAssistance.scss";
import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import {
	createContext,
	Dispatch,
	SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { isNurseAdmin } from "../../store/interfaces";
import { useSelector } from "react-redux";
import { AppState } from "../../store/reducer";
import { getHeaders, URI_API } from "../../utils/config";

import React from "react";
import MonthPicker from "./MonthPicker";
import CustomSnackbar, { SnackbarModes } from "../../widgets/Snackbars/Snackbar/Snackbar";

export const PlanificationContext = createContext<{
	role: string;
	setRole: Dispatch<SetStateAction<string>>;
	availableNurses: any[];
	setAvailableNurses: Dispatch<any>;
}>({
	role: "",
	setRole: () => {},
	availableNurses: [],
	setAvailableNurses: () => {},
});

const ClassX = [
	"AUX A",
	"AUX B",
	"AUX C",
	"COORD",
	"ENF A",
	"ENF B",
	"ENF C",
	"ENF D",
	"ENF ESP",
	"ENF SUB",
	"JEFE ENF",
];

export default function PlanificationAssistance() {
	const { role, availableNurses } = useContext(PlanificationContext);
	const [showModal, setShowModal] = useState(false);
	const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
	const [januaryMonth, setJanuaryMonth] = useState<any>([]);
	const [februaryMonth, setFebruaryMonth] = useState<any>([]);
	const [marchMonth, setMarchMonth] = useState<any>([]);
	const [aprilMonth, setAprilMonth] = useState<any>([]);
	const [mayMonth, setMayMonth] = useState<any>([]);
	const [juneMonth, setJuneMonth] = useState<any>([]);
	const [julyMonth, setJulyMonth] = useState<any>([]);
	const [augustMonth, setAugustMonth] = useState<any>([]);
	const [septemberMonth, setSeptemberMonth] = useState<any>([]);
	const [octoberMonth, setOctoberMonth] = useState<any>([]);
	const [novemberMonth, setNovemberMonth] = useState<any>([]);
	const [decemberMonth, setDecemberMonth] = useState<any>([]);
	const [januaryMonthOff, setJanuaryMonthOff] = useState<any>([]);
	const [februaryMonthOff, setFebruaryMonthOff] = useState<any>([]);
	const [marchMonthOff, setMarchMonthOff] = useState<any>([]);
	const [aprilMonthOff, setAprilMonthOff] = useState<any>([]);
	const [mayMonthOff, setMayMonthOff] = useState<any>([]);
	const [juneMonthOff, setJuneMonthOff] = useState<any>([]);
	const [julyMonthOff, setJulyMonthOff] = useState<any>([]);
	const [augustMonthOff, setAugustMonthOff] = useState<any>([]);
	const [septemberMonthOff, setSeptemberMonthOff] = useState<any>([]);
	const [octoberMonthOff, setOctoberMonthOff] = useState<any>([]);
	const [novemberMonthOff, setNovemberMonthOff] = useState<any>([]);
	const [decemberMonthOff, setDecemberMonthOff] = useState<any>([]);
	const [nurseNomina, setNurseNomina] = useState<any>("");
	const [selectedNurse, setSelectedNurse] = useState<any>({});
	const [, updateState] = useState<any>();
	const [nurseClassxSelected, setNurseClassxSelected] = useState({
		class: "",
	});
	const [newClassxSelected, setNewClassxSelected] = useState("");
	const [nursesByHospital, setNursesByHospital] = useState([]);
	const [openDownloadSnackbar, setOpenDownloadSnackbar] = useState(false);
	const [openChangesConfirmationSnackbar, setOpenChangesConfirmationSnackbar] = useState(false);
	const [openExcelConfirmationSnackbar, setOpenExcelConfirmationSnackbar] = useState(false);
	const [openExcelErrorSnackbar, setOpenExcelErrorSnackbar] = useState(false);

	const [loadingAutocompleteSelectNurse, setLoadingAutocompleteSelectNurse] =
		useState<boolean>(false);
	const [userHospital, setUserHospital] = useState("");
	const fileRef = useRef<HTMLInputElement>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [excelLineError, setExcelLineError] = useState<string>("");

	const toggleDays = (event: React.MouseEvent<HTMLElement>, newValue: string): void => {
		if (newValue !== null) setSelectedDaysType(newValue);
	};

	const isAdmin: boolean =
		isNurseAdmin(useSelector((state: AppState) => state.loggedUser).position!) || false;

	const [selectedDaysType, setSelectedDaysType] = useState<string>(
		isAdmin ? "laboral" : "disabled",
	);

	const getNurses = async () => {
		setLoadingAutocompleteSelectNurse(true);
		try {
			const response = await fetch(`${URI_API}/getNursesByHospital`, getHeaders());
			const data = await response.json();
			setNursesByHospital(data);
		} catch (error) {
			console.error("Error fetching nurses:", error);
		} finally {
			setLoadingAutocompleteSelectNurse(false);
		}
	};

	const selectHospital: { [hospital: string]: string } = {
		"Alta Especialidad":
			"https://drive.google.com/uc?export=download&id=149Z_JVDjATDgPogBrRPxTSMH72APHKNl",
		Saltillo:
			"https://drive.google.com/uc?export=download&id=1BNrOPmV1nu9rCwkj-CJiTao-NWGXM5No",
		"San Nicolás":
			"https://drive.google.com/uc?export=download&id=1DFq-jGBGXocDbW35JH5fi7O2HjPbTDeD",
		Altagracia:
			"https://drive.google.com/uc?export=download&id=1C6vGdqXsIRcTvfP0Ves4nzrWnwnC_jOn",
		"San Pedro":
			"https://drive.google.com/uc?export=download&id=1pyUTtWZSX-BumzowiX2LWgSzdkilQW-_",
		Sur: "https://drive.google.com/uc?export=download&id=17EWAUAE0D5DoRCenAe6Zdy1qX2WlT35L",
		Conchita:
			"https://drive.google.com/uc?export=download&id=1gZtOdcwdSSYrbPeMd_SLHDytzVARXkAT",
		Reynosa: "https://drive.google.com/uc?export=download&id=1iFvI3M_RFSn7BlI13QvYLoKBqoZWA-ye",
		Cumbres: "https://drive.google.com/uc?export=download&id=1bMOr9WkAQHz6I7sKUirYPRsdMwMlB1w6",
	};

	const handleTemplateDownload = () => {
		setOpenDownloadSnackbar(true);
		window.location.href = selectHospital[userHospital];
	};

	const sendChanges = () => {
		setIsLoading(true);

		const mergeAndFormatDates = (months: Date[][]): string[] => {
			return months.flat().map((day) => {
				const year = day.getFullYear();
				const month = String(day.getMonth() + 1).padStart(2, "0");
				const date = String(day.getDate()).padStart(2, "0");
				return `${year}-${month}-${date}`;
			});
		};

		const changedDays = mergeAndFormatDates([
			januaryMonth,
			februaryMonth,
			marchMonth,
			aprilMonth,
			mayMonth,
			juneMonth,
			julyMonth,
			augustMonth,
			septemberMonth,
			octoberMonth,
			novemberMonth,
			decemberMonth,
		]);

		const changedDaysOffs = mergeAndFormatDates([
			januaryMonthOff,
			februaryMonthOff,
			marchMonthOff,
			aprilMonthOff,
			mayMonthOff,
			juneMonthOff,
			julyMonthOff,
			augustMonthOff,
			septemberMonthOff,
			octoberMonthOff,
			novemberMonthOff,
			decemberMonthOff,
		]);

		const payload = {
			days: changedDays,
			nomina: nurseNomina,
			work_schedule_id: selectedNurse.work_schedule_id,
			nurse_id: selectedNurse.nurse_id,
			classx: newClassxSelected,
			days_offs: changedDaysOffs,
		};

		const saveChanges = async () => {
			try {
				const response = await fetch(
					`${URI_API}/nurse/save_changes`,
					getHeaders("POST", payload),
				);
				const data = await response.json();
				setOpenChangesConfirmationSnackbar(true);
			} catch (error) {
				console.log("Error saving changes: ", error);
			} finally {
				setIsLoading(false);
			}
		};

		saveChanges();
	};

	const getUserHospital = async () => {
		try {
			const response = await fetch(`${URI_API}/get_user_hospital`, getHeaders());
			const data = await response.json();
			setUserHospital(data.name);
		} catch (error) {
			console.error("Error getting hospital: ", error);
		}
	};

	useEffect(() => {
		getNurses();
		getUserHospital();
	}, []);

	useEffect(() => {
		setShowModal(role !== "");
	}, [role]);

	const forceUpdate = useCallback(() => {
		updateState({});
	}, []);

	const getNurseClassx = async (nomina: any): Promise<void> => {
		try {
			const response = await fetch(`${URI_API}/nurses`, getHeaders());
			const data = await response.json();
			const selectedNurse = data.find((nurse: any) => nurse.payroll === nomina);
			setNurseClassxSelected(selectedNurse);
		} catch (error) {
			console.error("Error fetching nurse class:", error);
		}
	};

	const onSelectNurse = async (nomina: any): Promise<void> => {
		forceUpdate();
		try {
			const response = await fetch(
				`${URI_API}/nurse/schedule_info?` + new URLSearchParams({ nomina }),
				getHeaders(),
			);
			const data = await response.json();
			setSelectedNurse(data);

			const months: Date[][] = Array.from({ length: 12 }, () => []);
			const monthsOff: Date[][] = Array.from({ length: 12 }, () => []);

			data.Dias_Trabajo.forEach((fecha: string) => {
				const aux = new Date(fecha);
				months[aux.getMonth()].push(aux);
			});

			data.Dias_Descanso.forEach((fecha: string) => {
				const aux = new Date(fecha);
				monthsOff[aux.getMonth()].push(aux);
			});

			const setters = [
				setJanuaryMonth,
				setFebruaryMonth,
				setMarchMonth,
				setAprilMonth,
				setMayMonth,
				setJuneMonth,
				setJulyMonth,
				setAugustMonth,
				setSeptemberMonth,
				setOctoberMonth,
				setNovemberMonth,
				setDecemberMonth,
			];

			const settersOff = [
				setJanuaryMonthOff,
				setFebruaryMonthOff,
				setMarchMonthOff,
				setAprilMonthOff,
				setMayMonthOff,
				setJuneMonthOff,
				setJulyMonthOff,
				setAugustMonthOff,
				setSeptemberMonthOff,
				setOctoberMonthOff,
				setNovemberMonthOff,
				setDecemberMonthOff,
			];

			months.forEach((month, index) => setters[index](month));
			monthsOff.forEach((monthOff, index) => settersOff[index](monthOff));

			forceUpdate();
		} catch (error) {
			console.error("Error fetching nurse schedule info:", error);
		}
	};

	const handleNurseChange = (e: any, newValue: any) => {
		if (newValue) {
			setIsLoading(true);
			setNurseNomina(newValue.payroll);
			setNewClassxSelected(nurseClassxSelected.class);

			Promise.all([
				onSelectNurse(newValue.payroll),
				getNurseClassx(newValue.payroll),
			]).finally(() => setIsLoading(false));
		}
	};

	const isLaboralDay = (): boolean => {
		return selectedDaysType === "laboral";
	};

	const months = [
		{
			monthIndex: 0,
			selectedDays: isLaboralDay() ? januaryMonth : januaryMonthOff,
			setSelectedDays: isLaboralDay() ? setJanuaryMonth : setJanuaryMonthOff,
			dayOffModifier: isLaboralDay() ? januaryMonthOff : januaryMonth,
			dayModifier: isLaboralDay() ? januaryMonth : januaryMonthOff,
		},
		{
			monthIndex: 1,
			selectedDays: isLaboralDay() ? februaryMonth : februaryMonthOff,
			setSelectedDays: isLaboralDay() ? setFebruaryMonth : setFebruaryMonthOff,
			dayOffModifier: isLaboralDay() ? februaryMonthOff : februaryMonth,
			dayModifier: isLaboralDay() ? februaryMonth : februaryMonthOff,
		},
		{
			monthIndex: 2,
			selectedDays: isLaboralDay() ? marchMonth : marchMonthOff,
			setSelectedDays: isLaboralDay() ? setMarchMonth : setMarchMonthOff,
			dayOffModifier: isLaboralDay() ? marchMonthOff : marchMonth,
			dayModifier: isLaboralDay() ? marchMonth : marchMonthOff,
		},
		{
			monthIndex: 3,
			selectedDays: isLaboralDay() ? aprilMonth : aprilMonthOff,
			setSelectedDays: isLaboralDay() ? setAprilMonth : setAprilMonthOff,
			dayOffModifier: isLaboralDay() ? aprilMonthOff : aprilMonth,
			dayModifier: isLaboralDay() ? aprilMonth : aprilMonthOff,
		},
		{
			monthIndex: 4,
			selectedDays: isLaboralDay() ? mayMonth : mayMonthOff,
			setSelectedDays: isLaboralDay() ? setMayMonth : setMayMonthOff,
			dayOffModifier: isLaboralDay() ? mayMonthOff : mayMonth,
			dayModifier: isLaboralDay() ? mayMonth : mayMonthOff,
		},
		{
			monthIndex: 5,
			selectedDays: isLaboralDay() ? juneMonth : juneMonthOff,
			setSelectedDays: isLaboralDay() ? setJuneMonth : setJuneMonthOff,
			dayOffModifier: isLaboralDay() ? juneMonthOff : juneMonth,
			dayModifier: isLaboralDay() ? juneMonth : juneMonthOff,
		},
		{
			monthIndex: 6,
			selectedDays: isLaboralDay() ? julyMonth : julyMonthOff,
			setSelectedDays: isLaboralDay() ? setJulyMonth : setJulyMonthOff,
			dayOffModifier: isLaboralDay() ? julyMonthOff : julyMonth,
			dayModifier: isLaboralDay() ? julyMonth : julyMonthOff,
		},
		{
			monthIndex: 7,
			selectedDays: isLaboralDay() ? augustMonth : augustMonthOff,
			setSelectedDays: isLaboralDay() ? setAugustMonth : setAugustMonthOff,
			dayOffModifier: isLaboralDay() ? augustMonthOff : augustMonth,
			dayModifier: isLaboralDay() ? augustMonth : augustMonthOff,
		},
		{
			monthIndex: 8,
			selectedDays: isLaboralDay() ? septemberMonth : septemberMonthOff,
			setSelectedDays: isLaboralDay() ? setSeptemberMonth : setSeptemberMonthOff,
			dayOffModifier: isLaboralDay() ? septemberMonthOff : septemberMonth,
			dayModifier: isLaboralDay() ? septemberMonth : septemberMonthOff,
		},
		{
			monthIndex: 9,
			selectedDays: isLaboralDay() ? octoberMonth : octoberMonthOff,
			setSelectedDays: isLaboralDay() ? setOctoberMonth : setOctoberMonthOff,
			dayOffModifier: isLaboralDay() ? octoberMonthOff : octoberMonth,
			dayModifier: isLaboralDay() ? octoberMonth : octoberMonthOff,
		},
		{
			monthIndex: 10,
			selectedDays: isLaboralDay() ? novemberMonth : novemberMonthOff,
			setSelectedDays: isLaboralDay() ? setNovemberMonth : setNovemberMonthOff,
			dayOffModifier: isLaboralDay() ? novemberMonthOff : novemberMonth,
			dayModifier: isLaboralDay() ? novemberMonth : novemberMonthOff,
		},
		{
			monthIndex: 11,
			selectedDays: isLaboralDay() ? decemberMonth : decemberMonthOff,
			setSelectedDays: isLaboralDay() ? setDecemberMonth : setDecemberMonthOff,
			dayOffModifier: isLaboralDay() ? decemberMonthOff : decemberMonth,
			dayModifier: isLaboralDay() ? decemberMonth : decemberMonthOff,
		},
	];

	const getMonthName = (monthIndex: number): string => {
		const monthNames = [
			"Enero",
			"Febrero",
			"Marzo",
			"Abril",
			"Mayo",
			"Junio",
			"Julio",
			"Agosto",
			"Septiembre",
			"Octubre",
			"Noviembre",
			"Diciembre",
		];
		return monthNames[monthIndex];
	};

	const uploadExcel = async (file: any) => {
		setIsLoading(true);
		let formData = new FormData();
		formData.append("file", file, "file");

		try {
			const res = await fetch(`${URI_API}/process_excel`, {
				method: "POST",
				credentials: "include",
				body: formData,
			});
			const body = await res.json();
			if (res.status !== 200) {
				setExcelLineError(body.current_line);
				throw new Error("Error uploading file");
			}
			getNurses();
			setOpenExcelConfirmationSnackbar(true);
		} catch (error) {
			setOpenExcelErrorSnackbar(true);
		} finally {
			setIsLoading(false);
			if (fileRef.current) {
				fileRef.current.value = "";
			}
		}
	};

	return (
		<Box className="assist">
			<Box className="assist__title-row">
				<Box className="assist__title-row__left">
					<Box
						className="assist__title-row__left__circle"
						onClick={() => setCurrentYear(currentYear - 1)}
					>
						<ChevronLeft size={18} />
					</Box>

					<Dot />

					<Box
						className="assist__title-row__left__circle"
						onClick={() => setCurrentYear(currentYear + 1)}
					>
						<ChevronRight size={18} />
					</Box>

					<Typography className="assist__title-row__left-text">
						Planificación de Asistencia
					</Typography>

					<Dot />

					<Typography className="assist__title-row__left-year">{currentYear}</Typography>
				</Box>

				{isAdmin && (
					<Box className="assist__title-row__right">
						<Button
							disableRipple
							className="assist__title-row__right__download-button"
							onClick={handleTemplateDownload}
						>
							<Download />
							<Typography>Descarga la plantilla automatizada</Typography>
						</Button>
						<Button
							disableRipple
							className="assist__title-row__right__upload-button"
							onClick={() => {
								fileRef.current?.click();
							}}
						>
							<Share />
							<Typography>Subir documento</Typography>
						</Button>
						<input
							type="file"
							accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
							ref={fileRef}
							multiple={false}
							onChange={(event) => {
								let file = event.currentTarget.files![0];
								uploadExcel(file);
							}}
							hidden={true}
						/>
					</Box>
				)}
			</Box>
			<Box className="assist__filters">
				<Box className="assist__filters__professional">
					<Typography>Profesional</Typography>
					<Autocomplete
						id="seleccionarprofesional"
						options={nursesByHospital.sort((a: any, b: any) =>
							a.name.localeCompare(b.name),
						)}
						getOptionLabel={(option: any) => option.name}
						onChange={handleNurseChange}
						loading={loadingAutocompleteSelectNurse}
						renderInput={(params) => (
							<TextField
								variant="outlined"
								className="assist__filters__professional-textfield"
								placeholder="Seleccionar"
								{...params}
								InputLabelProps={{ shrink: false }}
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<React.Fragment>
											{loadingAutocompleteSelectNurse ? (
												<CircularProgress
													color="inherit"
													size={20}
												/>
											) : null}
											{params.InputProps.endAdornment}
										</React.Fragment>
									),
								}}
							/>
						)}
					/>
				</Box>

				<Box className="assist__filters__classx">
					<Typography>Categoría</Typography>
					<Autocomplete
						disabled={nurseNomina === ""}
						id="seleccionarclassx"
						options={ClassX.sort((a: any, b: any) => a.localeCompare(b))}
						getOptionLabel={(option: any) => option}
						onChange={(e, newValue) => {
							if (newValue != null) {
								setNewClassxSelected(newValue);
								setNurseClassxSelected({ class: newValue });
							} else {
								setNewClassxSelected("");
							}
						}}
						value={nurseClassxSelected != null ? nurseClassxSelected.class : ""}
						renderInput={(params) => (
							<TextField
								variant="outlined"
								className="assist__filters__professional-textfield"
								placeholder="Seleccionar"
								{...params}
								InputLabelProps={{ shrink: false }}
							/>
						)}
					/>
				</Box>

				{isAdmin ? (
					<>
						<Box className="assist__filters__daytype">
							<Typography>Tipo de día</Typography>
							<ToggleButtonGroup
								disabled={!isAdmin}
								value={isAdmin ? selectedDaysType : ["laboral", "no-laboral"]}
								exclusive
								onChange={toggleDays}
								size="small"
								className="assist__filters__daytype__toggle"
							>
								<ToggleButton
									disableRipple
									className={`assist__filters__daytype__toggle-${
										isAdmin
											? selectedDaysType === "laboral"
												? "active-laboral"
												: "disabled"
											: "no-admin-disabled"
									}`}
									aria-label="left aligned"
									value={"laboral"}
								>
									{selectedDaysType !== "no-laboral" && (
										<Box
											className={`assist__filters__daytype-icon-${
												selectedDaysType === "laboral"
													? "laboral"
													: "disabled"
											}`}
										>
											#
										</Box>
									)}
									<Typography>Días laborales</Typography>
								</ToggleButton>
								<ToggleButton
									disableRipple
									className={`assist__filters__daytype__toggle-${
										isAdmin
											? selectedDaysType === "no-laboral"
												? "active-no-laboral"
												: "disabled"
											: "disabled"
									}`}
									aria-label="right aligned"
									value={"no-laboral"}
								>
									{selectedDaysType === "no-laboral" && (
										<Box className="assist__filters__daytype-icon-no-laboral">
											#
										</Box>
									)}
									<Typography>Días no laborales</Typography>
								</ToggleButton>
							</ToggleButtonGroup>
						</Box>
						<Button
							disabled={!isAdmin || nurseNomina === ""}
							disableRipple
							className="assist__filters-button"
							onClick={sendChanges}
						>
							<Typography>Guardar cambios</Typography>
							<Save />
						</Button>
					</>
				) : (
					<Box sx={{ display: "flex", flex: "4" }} />
				)}
			</Box>

			<Box className="assist__calendar">
				{Array.from({ length: 3 }, (_, i) => (
					<Box
						className="assist__calendar__row"
						key={i}
					>
						{months
							.slice(i * 4, i * 4 + 4)
							.map(
								({
									monthIndex,
									selectedDays,
									setSelectedDays,
									dayOffModifier,
									dayModifier,
								}) => (
									<Box
										className="assist__calendar__row__month"
										key={monthIndex}
									>
										<Typography className="assist__calendar__row__month-title">
											{getMonthName(monthIndex)}
										</Typography>
										<Box className="assist__calendar__row__month__days">
											<Typography>DOM</Typography>
											<Typography>LUN</Typography>
											<Typography>MAR</Typography>
											<Typography>MIÉ</Typography>
											<Typography>JUE</Typography>
											<Typography>VIE</Typography>
											<Typography>SÁB</Typography>
										</Box>
										<MonthPicker
											key={monthIndex}
											monthIndex={monthIndex}
											selectedDays={selectedDays}
											setSelectedDays={setSelectedDays}
											isLaboralDay={isLaboralDay()}
											isAdmin={isAdmin}
											dayOffModifier={dayOffModifier}
											dayModifier={dayModifier}
											selectedYear={currentYear}
										/>
									</Box>
								),
							)}
					</Box>
				))}
			</Box>

			{isLoading && (
				<Box className="loader-overlay">
					<Box className="custom-loader" />
				</Box>
			)}
			{openDownloadSnackbar && (
				<CustomSnackbar
					open={openDownloadSnackbar}
					onClose={() => setOpenDownloadSnackbar(false)}
					title="Iniciando descarga"
					subtitle="Esto tardará solo un momento."
					mode={SnackbarModes.DOWNLOAD}
				/>
			)}
			{openChangesConfirmationSnackbar && (
				<CustomSnackbar
					open={openChangesConfirmationSnackbar}
					onClose={() => setOpenChangesConfirmationSnackbar(false)}
					title="Cambios guardados con éxito"
					subtitle="Se actualizó la información de asistencia."
					mode={SnackbarModes.SUCCESS}
				/>
			)}
			{openExcelConfirmationSnackbar && (
				<CustomSnackbar
					open={openExcelConfirmationSnackbar}
					onClose={() => setOpenExcelConfirmationSnackbar(false)}
					title="Documento subido exitosamente"
					subtitle="Se actualizó la información de asistencia."
					mode={SnackbarModes.SUCCESS}
				/>
			)}
			{openExcelErrorSnackbar && (
				<CustomSnackbar
					open={openExcelErrorSnackbar}
					onClose={() => setOpenExcelErrorSnackbar(false)}
					title="Error al subir el documento"
					subtitle={`Error encontrado en la línea: ${excelLineError}`}
					mode={SnackbarModes.ERROR}
				/>
			)}
		</Box>
	);
}
